import { TreeDataNode } from '@livv/models';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowRight from '@mui/icons-material/ArrowRight';
import { Box, SxProps, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { Dispatch, FC, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import { MiniSearchBox } from '@components/search';
import useAmplitude from '@utils/hooks/useAmplitude';
import { sxs } from '@utils/mui/sx';
import { treeSearch } from '@utils/trees';
import { TrackingEventName } from '@utils/types/amplitude/events';

interface TreeViewSearchProps extends PropsWithChildren {
    expanded: string[];
    focusedNodeIds: string[];
    overrideSx?: SxProps;
    searchTerm: string;
    setExpanded: Dispatch<SetStateAction<string[]>>;
    setSearchTerm: Dispatch<SetStateAction<string>>;
    setSelectedNode?: Dispatch<SetStateAction<string>>;
    treeDataNodes: TreeDataNode[];
}

const TreeViewSearch: FC<TreeViewSearchProps> = ({
    children,
    expanded,
    focusedNodeIds,
    overrideSx = {},
    searchTerm,
    setExpanded,
    setSearchTerm,
    setSelectedNode = () => {},
    treeDataNodes,
}) => {
    const { t } = useTranslation('books');
    const { track } = useAmplitude();
    const { route } = useRouter();
    const [foundNodeCount, setFoundNodeCount] = useState<number | undefined>();
    const [focusedNode, setFocusedNode] = useState<string>(
        focusedNodeIds[focusedNodeIds.length - 1],
    );

    useEffect(() => {
        setFocusedNode(focusedNodeIds[focusedNodeIds.length - 1]);
    }, [focusedNodeIds]);

    const handleSearch = (term: string): void => {
        setSearchTerm(term);
        if (!term) {
            setFoundNodeCount(undefined);
            setExpanded(focusedNodeIds);

            return;
        }
        const foundPaths: string[][] = [];
        treeSearch(treeDataNodes, term, foundPaths);
        if (foundPaths.length) {
            setFoundNodeCount(foundPaths.length);
            const allNodesToExpand = uniq(flatten(foundPaths));
            setExpanded(allNodesToExpand);
        } else {
            setFoundNodeCount(0);
            setExpanded(focusedNodeIds);
        }
    };

    const handleAmplitudeEvent = (nodeIds: string[]) => {
        if (route.includes('tree-view')) {
            track({
                name: TrackingEventName.THEMATIC_PLAN_TREE_NODE_COLLAPSE_EXPAND_BUTTON_CLICKED,
                params: {
                    direction: nodeIds.length > expanded.length ? 'expand' : 'collapse',
                },
            });
        }
    };

    return (
        <>
            <MiniSearchBox onSearch={handleSearch} placeholder={t('summarySearch.placeholder')} />
            <Box display="flex" justifyContent="flex-end" mt={2}>
                {foundNodeCount !== undefined && (
                    <Typography
                        data-test-id="treeSearchResults"
                        sx={{
                            fontStyle: 'italic',
                            mt: 1,
                        }}
                        variant="body2"
                    >
                        {t('summarySearch', { count: foundNodeCount, searchTerm })}
                    </Typography>
                )}
            </Box>
            <SimpleTreeView
                aria-label={t('summary')}
                expandedItems={expanded}
                multiSelect={false}
                onExpandedItemsChange={(_, itemIds: string[]) => {
                    handleAmplitudeEvent(itemIds);
                    setExpanded(itemIds);
                }}
                onSelectedItemsChange={(_, itemIds: string | null) => {
                    if (itemIds) {
                        setSelectedNode(itemIds);
                    } else {
                        setSelectedNode('');
                    }
                }}
                selectedItems={focusedNode}
                slots={{ collapseIcon: ArrowDropDown, expandIcon: ArrowRight }}
                sx={sxs([
                    {
                        flexGrow: 1,
                        overflowX: 'hidden',
                    },
                    overrideSx,
                ])}
            >
                {children}
            </SimpleTreeView>
        </>
    );
};

export default TreeViewSearch;
